import React from "react";
import { add, format } from "date-fns";
import { FormProvider } from "react-hook-form";
import { Button, Dialog, Form } from "@sscs/ui";
import { DATE_FIELD_FORMAT } from "@sscs/utils";
import { EXPIRATION_DATE_OPTIONS } from "../../constants";
import { useGenerateAccessTokenForm } from "../../hooks";
import { ExpirationDateOption } from "../../types";

type GenerateTokenProps = {
  isOpen: boolean;
  onClose: () => void;
  onGenerated: (token: string) => void;
};

const expiryRangeOptions = EXPIRATION_DATE_OPTIONS.map((option) => option.id);

const getExpiryRangeOptionLabel = (option: ExpirationDateOption["id"]) => {
  return (
    EXPIRATION_DATE_OPTIONS.find((item) => item.id === option)?.label ?? ""
  );
};

const GenerateTokenContent = ({
  onClose,
  onGenerated,
}: Omit<GenerateTokenProps, "isOpen">): React.JSX.Element => {
  const { errors, onSubmit, formMethods, isDateDisabled } =
    useGenerateAccessTokenForm(onGenerated);

  return (
    <FormProvider {...formMethods}>
      <Form.Form onSubmit={onSubmit} name="personal-access-token-form">
        <Dialog.Header>
          <Dialog.Header.Title>
            Generate personal access token
          </Dialog.Header.Title>
        </Dialog.Header>
        <Dialog.Body>
          <Dialog.Section>
            <Dialog.Section.Content>
              <Form.TextField
                label="Name"
                name="name"
                id="name"
                isFullWidth
                isRequired
                errors={errors}
              />
              <Form.FormRow>
                <Form.Autocomplete
                  label="Expiration date"
                  name="expiryRange"
                  id="expiryRange"
                  isFullWidth
                  isClearable={false}
                  isRequired
                  getOptionLabel={getExpiryRangeOptionLabel}
                  options={expiryRangeOptions}
                  errors={errors}
                />
                <Form.DatePicker
                  name="expiry"
                  id="expiry"
                  isDisabled={isDateDisabled}
                  min={format(add(new Date(), { days: 1 }), DATE_FIELD_FORMAT)}
                />
              </Form.FormRow>
            </Dialog.Section.Content>
          </Dialog.Section>
        </Dialog.Body>
        <Dialog.Footer>
          <Dialog.Footer.Actions>
            <Button color="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Generate</Button>
          </Dialog.Footer.Actions>
        </Dialog.Footer>
      </Form.Form>
    </FormProvider>
  );
};

const GenerateToken = ({
  isOpen,
  ...props
}: GenerateTokenProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose}>
    <GenerateTokenContent {...props} />
  </Dialog.Frame>
);

export default GenerateToken;
